import overlaySelfie from 'assets/images/overlay-selfie.png'
import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import useOnlineStatus from 'hooks/useOnlineStatus'
import usePartialUpload from 'hooks/usePartialUpload'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  BookOpen,
  CheckCircle,
  Clock,
  Image,
  LogOut,
  RefreshCw,
  X,
} from 'react-feather'
import { useDispatch } from 'react-redux'
import Webcam from 'react-webcam'
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  Row,
  Spinner,
} from 'reactstrap'
import '../center-attendance/center-attendance.css'
import {
  checkInOrOut,
  fetchAttendance,
  setAttendanceNotes,
} from './attendanceSlice'

export default function Attendance() {
  const webcamRef = useRef(null)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const [note, setNote] = useState('')
  const [isCheckedIn, setIsCheckedIn] = useState(false)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const { uploadFile } = usePartialUpload()
  const [progress, setProgress] = useState(0)
  const [image, setImage] = useState(null)
  const [selectedAttendance, setSelectedAttendance] = useState({})
  const [showModal, setShowModal] = useState({
    note: false,
  })

  const [cameraPermission, setCameraPermission] = useState('checking')
  const [locationPermission, setLocationPermission] = useState('checking')
  const [cameraError, setCameraError] = useState('')
  const [isRetrying, setIsRetrying] = useState(false)
  const streamRef = useRef(null)
  const permissionCheckInterval = useRef(null)
  const locationCheckTimeout = useRef(null)
  const cameraCheckTimeout = useRef(null)
  const lastLocationCheck = useRef(0)
  const lastCameraCheck = useRef(0)

  const isOnline = useOnlineStatus()
  const wasOffline = useRef(!isOnline)
  const didMount = useRef(false)

  const setLocationPermissionDebounced = useCallback((newState) => {
    if (locationCheckTimeout.current) {
      clearTimeout(locationCheckTimeout.current)
    }

    locationCheckTimeout.current = setTimeout(() => {
      setLocationPermission((prev) => {
        if (prev !== newState) {
          return newState
        }
        return prev
      })
    }, 300)
  }, [])

  const setCameraPermissionDebounced = useCallback((newState) => {
    if (cameraCheckTimeout.current) {
      clearTimeout(cameraCheckTimeout.current)
    }

    cameraCheckTimeout.current = setTimeout(() => {
      setCameraPermission((prev) => {
        if (prev !== newState) {
          return newState
        }
        return prev
      })
    }, 200)
  }, [])

  const cleanupCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop()
      })
      streamRef.current = null
    }
  }, [])

  const checkLocationPermissionThrottled = useCallback(async () => {
    const now = Date.now()
    if (now - lastLocationCheck.current < 2000) {
      return
    }
    lastLocationCheck.current = now

    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationPermissionDebounced('denied')
        resolve(false)
        return
      }

      const isBackgroundCheck = locationPermission !== 'checking'
      if (!isBackgroundCheck) {
        setLocationPermission('checking')
      }

      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationPermissionDebounced('granted')
          resolve(true)
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermissionDebounced('denied')
            resolve(false)
          } else {
            setLocationPermissionDebounced('granted')
            resolve(true)
          }
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 60000,
        },
      )
    })
  }, [locationPermission, setLocationPermissionDebounced])
  const checkCameraPermission = useCallback(
    async (showAlert = false) => {
      const now = Date.now()
      if (now - lastCameraCheck.current < 3000) {
        return false
      }
      lastCameraCheck.current = now

      try {
        const isBackgroundCheck = cameraPermission !== 'checking'
        if (!isBackgroundCheck) {
          setCameraPermission('checking')
        }
        setCameraError('')

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          setCameraError('Browser tidak mendukung akses kamera')
          setCameraPermissionDebounced('denied')
          return false
        }

        cleanupCameraStream()

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user',
          },
        })

        streamRef.current = stream
        setCameraPermissionDebounced('granted')
        setCameraError('')

        if (!webcamRef.current) {
          setTimeout(() => {
            cleanupCameraStream()
          }, 100)
        }

        return true
      } catch (error) {
        console.error('Camera permission error:', error)

        let errorMessage = ''
        let permissionState = 'denied'

        switch (error.name) {
          case 'NotAllowedError':
            errorMessage =
              'Akses kamera ditolak. Silakan aktifkan izin kamera di browser.'
            permissionState = 'denied'
            break
          case 'NotFoundError':
            errorMessage =
              'Kamera tidak ditemukan. Pastikan kamera terpasang dan tidak digunakan aplikasi lain.'
            break
          case 'NotReadableError':
            errorMessage =
              'Kamera sedang digunakan aplikasi lain. Tutup aplikasi lain yang menggunakan kamera.'
            break
          case 'OverconstrainedError':
            errorMessage = 'Kamera tidak mendukung konfigurasi yang diminta.'
            break
          case 'SecurityError':
            errorMessage =
              'Akses kamera tidak diizinkan karena alasan keamanan.'
            break
          case 'AbortError':
            errorMessage = 'Akses kamera dibatalkan.'
            break
          default:
            errorMessage = `Error kamera: ${error.message || 'Tidak dapat mengakses kamera'}`
            break
        }

        setCameraError(errorMessage)
        setCameraPermissionDebounced(permissionState)

        if (showAlert && errorMessage) {
          alert(errorMessage)
        }

        return false
      }
    },
    [cleanupCameraStream, cameraPermission, setCameraPermissionDebounced],
  )

  const checkLocationPermission = useCallback(() => {
    return checkLocationPermissionThrottled()
  }, [checkLocationPermissionThrottled])

  const retryCameraAccess = useCallback(async () => {
    setIsRetrying(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      const success = await checkCameraPermission(true)

      if (success) {
        enqueueSnackbar('Kamera berhasil diaktifkan', { variant: 'success' })
      }
    } catch (error) {
      console.error('Retry camera failed:', error)
    } finally {
      setIsRetrying(false)
    }
  }, [checkCameraPermission, enqueueSnackbar])

  const monitorPermissions = useCallback(() => {
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'camera' })
        .then((status) => {
          const updateCameraPermission = () => {
            if (status.state === 'granted') {
              checkCameraPermission()
            } else if (status.state === 'denied') {
              setCameraPermission('denied')
              setCameraError('Izin kamera ditolak')
              cleanupCameraStream()
            } else {
              setCameraPermission('prompt')
            }
          }

          updateCameraPermission()

          status.onchange = updateCameraPermission
        })
        .catch(() => {
          checkCameraPermission()
        })

      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => {
          const updateLocationPermission = async () => {
            if (status.state === 'granted') {
              setLocationPermission('granted')
            } else if (status.state === 'denied') {
              setLocationPermission('denied')
            } else {
              await checkLocationPermission()
            }
          }

          updateLocationPermission()

          status.onchange = () => {
            setTimeout(updateLocationPermission, 100)
          }

          const isFirefox = navigator.userAgent
            .toLowerCase()
            .includes('firefox')
          if (isFirefox) {
            const firefoxLocationCheck = setInterval(async () => {
              if (document.visibilityState === 'visible') {
                const currentState = status.state
                if (currentState !== 'denied') {
                  const hasPermission = await new Promise((resolve) => {
                    navigator.geolocation.getCurrentPosition(
                      () => resolve(true),
                      (error) =>
                        resolve(error.code !== error.PERMISSION_DENIED),
                      { timeout: 3000, enableHighAccuracy: false },
                    )
                  })

                  const newState = hasPermission ? 'granted' : 'denied'
                  if (newState !== locationPermission) {
                    setLocationPermission(newState)
                  }
                }
              }
            }, 5000)
            return () => {
              clearInterval(firefoxLocationCheck)
            }
          }
        })
        .catch(() => {
          checkLocationPermission()
        })
    } else {
      checkCameraPermission()
      checkLocationPermission()
    }
  }, [
    checkCameraPermission,
    checkLocationPermission,
    cleanupCameraStream,
    locationPermission,
  ])

  const startPeriodicPermissionCheck = useCallback(() => {
    if (permissionCheckInterval.current) {
      clearInterval(permissionCheckInterval.current)
    }

    permissionCheckInterval.current = setInterval(async () => {
      if (document.visibilityState !== 'visible') return

      if (cameraPermission === 'denied') {
        checkCameraPermission()
      }

      if (locationPermission === 'denied') {
        checkLocationPermissionThrottled()
      }
    }, 15000)
  }, [
    cameraPermission,
    locationPermission,
    checkCameraPermission,
    checkLocationPermissionThrottled,
  ])

  useEffect(() => {
    const attendanceRaw = localStorage.getItem('attendance_history') || '[]'
    const attendanceList = JSON.parse(attendanceRaw)

    const hasPendingCheckInUpload = attendanceList?.some(
      (item) => item?.pendingCheckInUpload === true,
    )

    const hasPendingCheckOutUpload = attendanceList?.some(
      (item) => item?.pendingCheckOutUpload === true,
    )

    const handleAttendanceCheck = () => {
      if (!hasPendingCheckInUpload && !hasPendingCheckOutUpload) {
        getAttendance()
      } else {
        processPendingUploads()
      }
    }

    if (!didMount.current) {
      handleAttendanceCheck()
      didMount.current = true
      wasOffline.current = !isOnline
      return
    }

    if (isOnline && wasOffline.current) {
      enqueueSnackbar('Anda sudah online', { variant: 'success' })
      handleAttendanceCheck()
    }

    if (!isOnline) {
      enqueueSnackbar('Anda sedang offline', { variant: 'error' })
    }
    wasOffline.current = !isOnline
  }, [isOnline])

  useEffect(() => {
    if (Array.isArray(history) && history.length > 0) {
      const latest = history[0]
      setIsCheckedIn(!!latest.checkIn && !latest.checkOut)
    } else {
      setIsCheckedIn(false)
    }
  }, [history])

  useEffect(() => {
    monitorPermissions()
    startPeriodicPermissionCheck()

    return () => {
      if (permissionCheckInterval.current) {
        clearInterval(permissionCheckInterval.current)
      }
      cleanupCameraStream()
    }
  }, [])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => {
          monitorPermissions()
        }, 1000)
      } else {
        cleanupCameraStream()
      }
    }

    const handleFocus = () => {
      setTimeout(() => {
        monitorPermissions()
      }, 1500)
    }

    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)

      if (locationCheckTimeout.current) {
        clearTimeout(locationCheckTimeout.current)
      }
      if (cameraCheckTimeout.current) {
        clearTimeout(cameraCheckTimeout.current)
      }
    }
  }, [monitorPermissions, cleanupCameraStream])

  const getCurrentLocation = () =>
    new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) =>
          resolve({
            latitude: coords.latitude,
            longitude: coords.longitude,
          }),
        (error) => {
          alert(
            'Gagal mendapatkan lokasi. Pastikan Anda telah mengaktifkan izin lokasi di browser atau perangkat.',
          )
          resolve({
            latitude: '-',
            longitude: '-',
          })
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      )
    })

  const getAttendance = async () => {
    try {
      const response = await dispatch(
        fetchAttendance({ page: 0, size: 5 }),
      ).unwrap()

      setHistory(response?.items)
      localStorage.setItem(
        'attendance_history',
        JSON.stringify(response?.items),
      )
    } catch {
      // Error fetching attendance
    }
  }

  function base64ToFile(base64Data, filename) {
    const arr = base64Data.split(',')
    const mime = arr[0].match(/:(.*?);/)[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) u8arr[n] = bstr.charCodeAt(n)
    return new File([u8arr], filename, { type: mime })
  }

  async function processPendingUploads() {
    if (!navigator.onLine) {
      return
    }

    const raw = localStorage.getItem('attendance_history') || '[]'
    let currentEntries = []
    try {
      currentEntries = JSON.parse(raw)
    } catch (e) {
      currentEntries = []
    }

    if (!Array.isArray(currentEntries) || currentEntries.length === 0) {
      return
    }

    const pendingEntries = currentEntries
      .filter((e) => e.pendingCheckInUpload || e.pendingCheckOutUpload)
      .sort(
        (a, b) => new Date(a.checkIn).getTime() - new Date(b.checkIn).getTime(),
      )

    if (pendingEntries.length === 0) {
      return
    }

    let successCount = 0
    let hasError = false

    for (const entry of pendingEntries) {
      if (!navigator.onLine) {
        hasError = true
        break
      }

      const {
        id,
        photoIn,
        photoOut,
        pendingCheckInUpload,
        pendingCheckOutUpload,
        latitudeIn,
        longitudeIn,
        latitudeOut,
        longitudeOut,
        checkIn,
        checkOut,
        notes,
      } = entry

      try {
        if (pendingCheckInUpload && photoIn) {
          let file = photoIn
          if (typeof photoIn === 'string' && photoIn.startsWith('data:image')) {
            file = base64ToFile(photoIn, `checkin-${Date.now()}.jpg`)
          }

          const uploaded = await uploadFile(
            file,
            (p) => setProgress?.(p),
            'USER_ID',
            'ATTENDANCE',
            true,
          )

          if (!uploaded?.url) throw new Error('Upload check-in gagal.')

          let lat = latitudeIn,
            lng = longitudeIn
          if (!lat || lat === '-' || !lng || lng === '-') {
            const location = await getCurrentLocation()
            lat = location.latitude
            lng = location.longitude
          }

          await dispatch(
            checkInOrOut({
              photo: uploaded.url,
              latitude: lat,
              longitude: lng,
              notes: notes?.[0]?.notes,
              type: 'checkin',
              createdAt: checkIn,
            }),
          ).unwrap()
        }

        if (pendingCheckOutUpload && photoOut) {
          let file = photoOut
          if (
            typeof photoOut === 'string' &&
            photoOut.startsWith('data:image')
          ) {
            file = base64ToFile(photoOut, `checkout-${Date.now()}.jpg`)
          }

          const uploaded = await uploadFile(
            file,
            (p) => setProgress?.(p),
            'USER_ID',
            'ATTENDANCE',
            true,
          )

          if (!uploaded?.url) throw new Error('Upload check-out gagal.')

          let lat = latitudeOut,
            lng = longitudeOut
          if (!lat || lat === '-' || !lng || lng === '-') {
            const location = await getCurrentLocation()
            lat = location.latitude
            lng = location.longitude
          }

          await dispatch(
            checkInOrOut({
              photo: uploaded.url,
              latitude: lat,
              longitude: lng,
              notes: notes?.[0]?.notes,
              type: 'checkout',
              createdAt: checkOut,
            }),
          ).unwrap()
        }

        // Update localStorage item-by-item upon successful upload
        successCount++
        const latestRaw = localStorage.getItem('attendance_history') || '[]'
        let latestEntries = []
        try {
          latestEntries = JSON.parse(latestRaw)
        } catch (e) {
          latestEntries = []
        }

        const remainingEntries = latestEntries.filter((e) => e.id !== id)
        if (remainingEntries.length === 0) {
          localStorage.removeItem('attendance_history')
        } else {
          localStorage.setItem(
            'attendance_history',
            JSON.stringify(remainingEntries),
          )
        }
      } catch (error) {
        console.error(`[Error] Gagal memproses entry ID: ${entry.id}`, error)
        hasError = true
        break
      }
    }

    if (successCount > 0) {
      enqueueSnackbar(
        hasError
          ? `${successCount} data absensi offline berhasil di-upload`
          : 'Data offline berhasil di-upload',
        { variant: 'success' },
      )
      await getAttendance()
    } else if (hasError) {
      enqueueSnackbar('Gagal meng-upload data absensi offline', {
        variant: 'error',
      })
    }
  }

  const handleCheck = async () => {
    const type = isCheckedIn ? 'checkout' : 'checkin'

    if (cameraPermission !== 'granted') {
      alert('Kamera belum siap. Silakan aktifkan izin kamera terlebih dahulu.')
      return
    }

    if (!webcamRef.current) {
      alert('Webcam belum siap. Silakan tunggu sebentar atau refresh halaman.')
      return
    }

    const imageSrc = webcamRef.current.getScreenshot()
    setImage(imageSrc)

    if (!imageSrc) {
      alert('Gagal mengambil gambar dari webcam.')
      return
    }

    setLoading(true)

    try {
      const createdAt = new Date().toISOString()

      const getCurrentLocation = () =>
        new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            ({ coords }) =>
              resolve({
                latitude: coords.latitude,
                longitude: coords.longitude,
              }),
            () =>
              resolve({
                latitude: '-',
                longitude: '-',
              }),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
          )
        })

      const { latitude, longitude } = await getCurrentLocation()
      const file = dataURLtoFile(imageSrc, `selfie-${Date.now()}.jpg`)
      const currentHistory = JSON.parse(
        localStorage.getItem('attendance_history') || '[]',
      )

      let newHistoryItem = null

      if (navigator.onLine) {
        const uploaded = await uploadFile(
          file,
          (percentage) => setProgress(percentage),
          'USER_ID',
          'ATTENDANCE',
          true,
        )

        if (!uploaded?.url) {
          alert('Upload gambar gagal.')
          return
        }

        await dispatch(
          checkInOrOut({
            photo: uploaded.url,
            latitude,
            longitude,
            notes: note,
            type,
            createdAt,
          }),
        ).unwrap()

        await getAttendance()
      } else {
        const base64 = imageSrc
        const id = `local-${Date.now()}`

        if (
          type === 'checkout' &&
          currentHistory.length > 0 &&
          currentHistory[0].checkIn &&
          !currentHistory[0].checkOut
        ) {
          currentHistory[0] = {
            ...currentHistory[0],
            checkOut: createdAt,
            createdAt,
            photoOut: base64,
            latitudeOut: latitude,
            longitudeOut: longitude,
            isCheckOutUploaded: false,
            pendingCheckOutUpload: true,
            notes: note ? [{ ts: new Date(), notes: [note] }] : null,
            type: 'checkout',
          }

          newHistoryItem = currentHistory[0]
        } else {
          newHistoryItem = {
            id,
            photoIn: base64,
            latitudeIn: latitude,
            longitudeIn: longitude,
            notes: note ? [{ ts: new Date(), notes: [note] }] : null,
            createdAt,
            type: 'checkin',
            checkIn: createdAt,
            checkOut: null,
            isOffline: true,
            isCheckInUploaded: false,
            isCheckOutUploaded: false,
            pendingCheckInUpload: true,
            pendingCheckOutUpload: false,
            status: 'PENDING_UPLOAD',
          }

          currentHistory.unshift(newHistoryItem)
        }

        localStorage.setItem(
          'attendance_history',
          JSON.stringify(currentHistory),
        )
        setHistory(currentHistory)
      }

      setIsCheckedIn(type === 'checkin')
      if (note) setNote('')
    } catch (err) {
      console.error('Gagal proses check-in/out:', err)
      alert(err.message || 'Proses check-in/out gagal. Coba lagi nanti.')
    } finally {
      setImage(null)
      setLoading(false)
    }
  }

  const handleSaveNote = async () => {
    if (!note.trim()) return
    setLoading(true)
    try {
      await dispatch(setAttendanceNotes({ notes: note })).unwrap()
      setNote('')
      await getAttendance()

      setHistory((prev) => {
        if (!prev.length) return prev
        const updated = [...prev]
        updated[0] = { ...updated[0], isNewNote: true }
        return updated
      })
    } catch (err) {
      console.error('Gagal menyimpan catatan:', err)
    } finally {
      setLoading(false)
    }
  }

  const renderPermissionStatus = () => {
    const cameraReady = cameraPermission === 'granted'
    const locationReady = locationPermission === 'granted'

    if (cameraReady && locationReady) {
      return null
    }

    return (
      <div className="alert alert-warning mt-3" role="alert">
        <strong>Perhatian:</strong>

        {!cameraReady && (
          <div className="mt-2">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span>📹 Status Kamera: </span>
              <span
                className={`badge ${
                  cameraPermission === 'checking'
                    ? 'bg-info'
                    : cameraPermission === 'granted'
                      ? 'bg-success'
                      : cameraPermission === 'denied'
                        ? 'bg-danger'
                        : 'bg-warning'
                }`}
              >
                <P size="small">
                  {cameraPermission === 'checking'
                    ? 'Memeriksa...'
                    : cameraPermission === 'granted'
                      ? 'Aktif'
                      : cameraPermission === 'denied'
                        ? 'Ditolak'
                        : 'Perlu Izin'}
                </P>
              </span>
            </div>

            {cameraError && (
              <div className="alert alert-danger alert-sm mb-2">
                {cameraError}
              </div>
            )}

            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => checkCameraPermission(true)}
                disabled={cameraPermission === 'checking'}
                data-testid="attendance-camera-allow"
              >
                <P size="small">
                  {cameraPermission === 'checking'
                    ? 'Memeriksa...'
                    : 'Aktifkan Kamera'}
                </P>
              </button>

              {(cameraPermission === 'denied' || cameraError) && (
                <button
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                  onClick={retryCameraAccess}
                  disabled={isRetrying}
                  data-testid="attendance-camera-retry"
                >
                  <RefreshCw
                    size={16}
                    className={
                      isRetrying ? 'spinner-border spinner-border-sm' : ''
                    }
                  />
                  <P size="small">{isRetrying ? 'Mencoba...' : 'Coba Lagi'}</P>
                </button>
              )}
            </div>
          </div>
        )}

        {!locationReady && (
          <div className="mt-2">
            <div className="d-flex align-items-center gap-2 mb-2">
              <span>📍 Status Lokasi: </span>
              <span
                className={`badge ${
                  locationPermission === 'checking'
                    ? 'bg-info'
                    : locationPermission === 'granted'
                      ? 'bg-success'
                      : locationPermission === 'denied'
                        ? 'bg-danger'
                        : 'bg-warning'
                }`}
              >
                <P size="small">
                  {locationPermission === 'checking'
                    ? 'Memeriksa...'
                    : locationPermission === 'granted'
                      ? 'Aktif'
                      : locationPermission === 'denied'
                        ? 'Ditolak'
                        : 'Perlu Izin'}
                </P>
              </span>
            </div>

            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => {
                setLocationPermission('checking')
                checkLocationPermissionThrottled().then((granted) => {
                  if (!granted) {
                    alert(
                      'Lokasi belum siap. Silakan aktifkan izin lokasi terlebih dahulu.',
                    )
                  }
                })
              }}
              disabled={locationPermission === 'checking'}
              data-testid="attendance-location-allow"
            >
              <P size="small">
                {locationPermission === 'checking'
                  ? 'Memeriksa...'
                  : 'Aktifkan Lokasi'}
              </P>
            </button>
          </div>
        )}

        {/* Help text */}
        <div className="mt-3 small text-muted">
          <strong>Tips:</strong>
          <ul className="mb-0 mt-1">
            <li>Pastikan tidak ada aplikasi lain yang menggunakan kamera</li>
            <li>Coba refresh halaman jika kamera tidak muncul</li>
            <li>
              Periksa pengaturan browser dan izinkan akses kamera & lokasi
            </li>
            <li>Di Windows: Periksa pengaturan Privacy → Camera di sistem</li>
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className="w-100 px-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'Absensi'} />
      </div>

      {renderPermissionStatus()}

      <div
        style={{
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '24px 0',
        }}
      >
        <Row className="dashboard-row">
          <Col md={6} className="dashboard-col" style={{ paddingBottom: '0' }}>
            {/* Camera Card */}
            <Card
              style={{
                overflow: 'hidden',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: 'none',
                borderRadius: '16px',
                height: '100%',
                maxHeight: '800px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  backgroundColor: 'black',
                  minHeight: 0,
                }}
              >
                {image ? (
                  <img
                    src={image}
                    alt="Captured"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : cameraPermission === 'granted' ? (
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    mirrored={true}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    videoConstraints={{ facingMode: 'user' }}
                    onUserMediaError={(error) => {
                      console.error('Webcam error:', error)
                      setCameraError('Gagal mengakses kamera: ' + error.message)
                      setCameraPermission('denied')
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '350px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#1f2937',
                    }}
                  >
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#9ca3af',
                      }}
                    >
                      <div style={{ marginBottom: '12px', fontSize: '32px' }}>
                        📹
                      </div>
                      <div>
                        {cameraPermission === 'checking'
                          ? 'Memeriksa akses kamera...'
                          : cameraPermission === 'denied'
                            ? 'Kamera tidak dapat diakses'
                            : 'Menunggu izin kamera...'}
                      </div>
                    </div>
                  </div>
                )}

                {/* SVG Face Guide Overlay */}
                {cameraPermission === 'granted' && !image && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      paddingBottom: '80px',
                    }}
                  >
                    <svg
                      viewBox="0 0 200 250"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        width: '100%',
                        height: '100%',
                        maxWidth: '400px',
                        maxHeight: '50vh',
                        opacity: 0.6,
                        filter: 'drop-shadow(0px 0px 4px rgba(0,0,0,0.5))',
                      }}
                    >
                      <path
                        d="M100 20C60 20 30 50 30 90V110C30 140 50 165 75 175C60 185 40 200 30 230"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <path
                        d="M100 20C140 20 170 50 170 90V110C170 140 150 165 125 175C140 185 160 200 170 230"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                      <rect
                        x="10"
                        y="10"
                        width="180"
                        height="230"
                        rx="20"
                        stroke="white"
                        strokeWidth="1"
                        strokeDasharray="10 5"
                        opacity="0.5"
                      />
                    </svg>
                  </div>
                )}

                {/* Check-In/Out Button Overlay */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '24px',
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    color={isCheckedIn ? 'danger' : 'success'}
                    size="lg"
                    style={{
                      width: '100%',
                      padding: '16px',
                      borderRadius: '16px',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                    onClick={handleCheck}
                    disabled={
                      loading ||
                      cameraPermission !== 'granted' ||
                      locationPermission !== 'granted'
                    }
                    data-testid={
                      isCheckedIn
                        ? 'attendance-checkout-button'
                        : 'attendance-checkin-button'
                    }
                  >
                    {loading ? (
                      <Spinner size="sm" />
                    ) : isCheckedIn ? (
                      <LogOut size={24} />
                    ) : (
                      <CheckCircle size={24} />
                    )}
                    {loading
                      ? 'Memproses...'
                      : isCheckedIn
                        ? 'Check Out'
                        : 'Check In'}
                  </Button>
                </div>
              </div>

              {/* Notes Input Footer */}
              <CardBody
                style={{
                  backgroundColor: 'white',
                  padding: '24px',
                  flex: '0 0 auto',
                }}
              >
                <h6
                  style={{
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#374151',
                  }}
                >
                  Catatan Harian
                </h6>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Input
                    type="text"
                    placeholder="Tulis catatan aktivitas..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    style={{
                      backgroundColor: '#f9fafb',
                      borderColor: '#e5e7eb',
                      padding: '12px',
                      borderRadius: '12px',
                    }}
                    data-testid="attendance-note-input"
                  />
                  <Button
                    color="primary"
                    style={{
                      padding: '12px 32px',
                      borderRadius: '12px',
                      fontWeight: '600',
                    }}
                    onClick={handleSaveNote}
                    disabled={
                      loading ||
                      !note.trim() ||
                      !isCheckedIn ||
                      cameraPermission !== 'granted' ||
                      locationPermission !== 'granted' ||
                      !navigator.onLine
                    }
                    data-testid="attendance-note-save-button"
                  >
                    Kirim
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={6} className="dashboard-col" style={{ paddingBottom: '0' }}>
            {/* History Panel */}
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '24px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <h5
                style={{
                  fontWeight: 'bold',
                  color: '#1f2937',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    padding: '8px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '8px',
                    color: '#3b82f6',
                  }}
                >
                  <Clock size={20} />
                </div>
                Riwayat Absensi
              </h5>

              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '8px',
                }}
              >
                {!history || history.length === 0 ? (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#9ca3af',
                      padding: '48px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '16px',
                      border: '2px dashed #e5e7eb',
                      minHeight: '300px',
                    }}
                  >
                    <Clock
                      size={48}
                      style={{ marginBottom: '16px', opacity: 0.5 }}
                    />
                    <p style={{ fontSize: '16px', fontWeight: '500' }}>
                      Belum ada riwayat absensi hari ini
                    </p>
                  </div>
                ) : (
                  history.slice(0, 5).map((item, idx) => {
                    const hasOfflineCheckIn = item?.pendingCheckInUpload
                    const hasOfflineCheckOut = item?.pendingCheckOutUpload

                    return (
                      <div
                        key={idx}
                        style={{
                          backgroundColor:
                            hasOfflineCheckIn || hasOfflineCheckOut
                              ? '#fffbeb'
                              : '#f9fafb',
                          borderRadius: '16px',
                          padding: '20px',
                          marginBottom: '16px',
                          border: `1px solid ${hasOfflineCheckIn || hasOfflineCheckOut ? '#fcd34d' : '#f3f4f6'}`,
                          transition: 'all 0.2s',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '16px',
                            marginBottom:
                              item?.notes?.length > 0 ? '16px' : '0',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              className="d-flex align-items-center justify-content-between mb-1"
                              style={{ minHeight: '16px' }}
                            >
                              <div className="d-flex align-items-center gap-1">
                                <span
                                  style={{
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontWeight: '700',
                                    color: '#16a34a',
                                  }}
                                >
                                  Check In
                                </span>
                                {hasOfflineCheckIn && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      color: '#92400e',
                                      backgroundColor: '#fef3c7',
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    OFFLINE
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: '#6b7280',
                                }}
                              >
                                {moment(item.checkIn || item.createdAt).format(
                                  'DD MMM YYYY',
                                )}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '20px',
                                  fontFamily: 'monospace',
                                  fontWeight: '700',
                                  color: '#111827',
                                  backgroundColor: 'white',
                                  padding: '12px',
                                  borderRadius: '12px',
                                  border: '1px solid #e5e7eb',
                                  textAlign: 'center',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  flex: 1,
                                }}
                              >
                                {item.checkIn
                                  ? moment(item.checkIn).format('HH:mm')
                                  : '--:--'}
                              </div>
                              {item.photoIn && (
                                <Button
                                  outline
                                  color="secondary"
                                  size="sm"
                                  style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    borderColor: '#d1d5db',
                                    color: '#6b7280',
                                  }}
                                  onClick={() =>
                                    window.open(item.photoIn, '_blank')
                                  }
                                >
                                  <Image size={16} />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              className="d-flex align-items-center justify-content-between mb-1"
                              style={{ minHeight: '16px' }}
                            >
                              <div className="d-flex align-items-center gap-1">
                                <span
                                  style={{
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontWeight: '700',
                                    color: '#dc2626',
                                  }}
                                >
                                  Check Out
                                </span>
                                {hasOfflineCheckOut && (
                                  <span
                                    style={{
                                      fontSize: '9px',
                                      fontWeight: '700',
                                      color: '#92400e',
                                      backgroundColor: '#fef3c7',
                                      padding: '1px 4px',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    OFFLINE
                                  </span>
                                )}
                              </div>
                              {item.checkOut && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                  }}
                                >
                                  {moment(item.checkOut).format('DD MMM YYYY')}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '20px',
                                  fontFamily: 'monospace',
                                  fontWeight: '700',
                                  color: '#111827',
                                  backgroundColor: 'white',
                                  padding: '12px',
                                  borderRadius: '12px',
                                  border: '1px solid #e5e7eb',
                                  textAlign: 'center',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  flex: 1,
                                }}
                              >
                                {item.checkOut
                                  ? moment(item.checkOut).format('HH:mm')
                                  : '--:--'}
                              </div>
                              {item.photoOut && (
                                <Button
                                  outline
                                  color="secondary"
                                  size="sm"
                                  style={{
                                    padding: '8px',
                                    borderRadius: '8px',
                                    borderColor: '#d1d5db',
                                    color: '#6b7280',
                                  }}
                                  onClick={() =>
                                    window.open(item.photoOut, '_blank')
                                  }
                                >
                                  <Image size={16} />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {item?.notes?.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            {item.notes.map((noteItem, noteIdx) => (
                              <div
                                key={noteIdx}
                                style={{
                                  backgroundColor: '#fffbeb',
                                  padding: '8px 12px',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  color: '#92400e',
                                  border: '1px solid #fcd34d',
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                  marginBottom:
                                    noteIdx < item.notes.length - 1
                                      ? '6px'
                                      : '0',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  setShowModal((prev) => ({
                                    ...prev,
                                    note: true,
                                  }))
                                  setSelectedAttendance(item)
                                  setHistory((prev) =>
                                    prev.map((h) =>
                                      h.id === item.id
                                        ? { ...h, isNewNote: false }
                                        : h,
                                    ),
                                  )
                                }}
                              >
                                <div>📝</div>
                                <div
                                  style={{
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    opacity: 0.7,
                                    minWidth: '40px',
                                  }}
                                >
                                  {moment(noteItem.ts).format('HH:mm')}
                                </div>
                                <div style={{ flex: 1, lineHeight: '1.4' }}>
                                  "{noteItem.notes}"
                                </div>
                                {item?.isNewNote && noteIdx === 0 && (
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: 'red',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {renderModal()}
    </div>
  )

  function renderModal() {
    return (
      <>
        <Modal
          centered
          isOpen={showModal?.note}
          toggle={() => setShowModal((prev) => ({ ...prev, note: false }))}
        >
          <div
            className="p-3"
            style={{ maxHeight: '500px', overflowY: 'auto' }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <P size="large">Catatan</P>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setShowModal((prev) => ({ ...prev, note: false }))
                }
              >
                <X />
              </div>
            </div>
            {selectedAttendance?.notes?.length ? (
              <div className="mt-3">
                {selectedAttendance?.notes?.map((item, index) => {
                  return (
                    <li className="list-group-item text-break" key={index}>
                      <strong>
                        {moment(item.ts).format('dddd, DD MMM YYYY, HH:mm')}:
                      </strong>{' '}
                      {item.notes}
                    </li>
                  )
                })}
              </div>
            ) : (
              <div className="p-3 d-flex align-items-center justify-content-center">
                <P>Tidak ada catatan</P>
              </div>
            )}
          </div>
        </Modal>
      </>
    )
  }
}

function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)[1]
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) u8arr[n] = bstr.charCodeAt(n)
  return new File([u8arr], filename, { type: mime })
}
